import { CareProtocolApi, type CareEventInput } from "@/api/careProtocol/CareProtocolApi";
import {
  CasesApi,
  type AnimalCaseRow,
  type CreateAnimalCaseInput,
  type CreateAnimalCasesBatchInput,
} from "@/api/cases/CasesApi";
import { supabase } from "@/api/supabaseClient";
import { isNetworkError, postgresSqlState, uiErrorMessage } from "@/utils/isoDate";
import { OFFLINE_SYNC_HINT } from "./hints";
import { assertEventsAllowedOffline } from "../domain/allowedEvents";
import { newClientId, toAdditionalResubmitPayload } from "../domain/conflicts";
import type { OutboxItem } from "../domain/outboxScheduler";
import { isOfflineOnline } from "../networkFlag";
import { getOfflineStore } from "../store";
import { isOfflineSyncPaused } from "./syncPause";
import type { BundleCase, CareBundle, LocalCareEntry, OfflineStore } from "../store/types";
import { applyPulledBundle, drainOutbox, newOutboxId, type SyncResult } from "./engine";

export { OFFLINE_SYNC_HINT };

function nowIso(): string {
  return new Date().toISOString();
}

export function careSaveUiMessage(error: unknown): string {
  return uiErrorMessage(error, "Speichern fehlgeschlagen.");
}

function emptySync(): SyncResult {
  return {
    success: 0,
    failed: 0,
    conflicts: 0,
    quarantined: 0,
    errors: [],
    pullFailed: false,
  };
}

export const PULL_WARN_COOLDOWN_MS = 30 * 60 * 1000;

const PULL_NOTICE_LEAD = "Aktualisierung vom Server fehlgeschlagen";
const PULL_NOTICE_TAIL = "Ihre Einträge werden weiter übertragen.";

const pullWarnAt = new Map<string, number>();

export function resetPullWarnCooldownForTests(): void {
  pullWarnAt.clear();
}

async function hasAuthSession(): Promise<boolean> {
  if (isOfflineSyncPaused()) return false;
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) return false;
    return data.session != null;
  } catch {
    return false;
  }
}

async function enqueue(item: Omit<OutboxItem, "createdAt" | "updatedAt" | "error" | "conflictKind">) {
  const store = await getOfflineStore();
  const full: OutboxItem = {
    ...item,
    error: null,
    conflictKind: null,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  };
  await store.upsertOutbox(full);
  return full;
}

export async function submitCareEntryViaGateway(input: {
  caseId: string;
  clientEntryId: string;
  recordedAt: string;
  events: CareEventInput[];
}) {
  assertEventsAllowedOffline(input.events, isOfflineOnline());
  const store = await getOfflineStore();

  const runOnline = () =>
    CareProtocolApi.submitEntryOnline({
      caseId: input.caseId,
      clientEntryId: input.clientEntryId,
      recordedAt: input.recordedAt,
      events: input.events,
    });

  if (isOfflineOnline()) {
    try {
      return await runOnline();
    } catch (error) {
      if (!isNetworkError(error)) throw error;
    }
  }

  const pendingCreate = (await store.getOutbox()).find(
    (row) =>
      row.op === "create_animal_case" &&
      row.status !== "synced" &&
      (row.clientId === input.caseId || row.caseId === input.caseId)
  );
  await enqueue({
    id: newOutboxId(),
    op: "submit_care_entry",
    status: "pending",
    clientId: input.clientEntryId,
    caseId: input.caseId,
    dependsOn: pendingCreate ? [pendingCreate.id] : [],
    payload: {
      caseId: input.caseId,
      clientEntryId: input.clientEntryId,
      recordedAt: input.recordedAt,
      events: input.events,
    },
  });
  const entry: LocalCareEntry = {
    id: input.clientEntryId,
    case_id: input.caseId,
    client_entry_id: input.clientEntryId,
    recorded_at: input.recordedAt,
    received_at: null,
    author_id: null,
    events: input.events,
    syncStatus: "pending",
  };
  await store.appendLocalEntry(entry);
  return {
    id: input.clientEntryId,
    case_id: input.caseId,
    recorded_at: input.recordedAt,
    client_entry_id: input.clientEntryId,
    pending_sync: true,
  };
}

function localCaseFromInput(input: CreateAnimalCaseInput): BundleCase {
  return {
    id: input.clientCreateId,
    organization_id: input.organizationId,
    case_number: null,
    status: "in_care",
    name: input.name ?? null,
    sex: input.sex ?? "unknown",
    age_class: input.ageClass ?? "unclear",
    found_on: input.foundOn ?? null,
    current_care_site_id: input.careSiteId,
    current_place_id: input.placeId ?? null,
    organization_species_id: input.organizationSpeciesId ?? null,
    species_free_text: input.speciesFreeText?.trim() ? input.speciesFreeText.trim() : null,
    client_create_id: input.clientCreateId,
    created_at: nowIso(),
    pendingSync: true,
  };
}

export async function createCaseViaGateway(
  input: CreateAnimalCaseInput
): Promise<AnimalCaseRow> {
  if (isOfflineOnline()) {
    try {
      return await CasesApi.createCaseOnline(input);
    } catch (error) {
      if (!isNetworkError(error)) throw error;
    }
  }
  const store = await getOfflineStore();
  const local = localCaseFromInput(input);
  await enqueue({
    id: newOutboxId(),
    op: "create_animal_case",
    status: "pending",
    clientId: input.clientCreateId,
    caseId: input.clientCreateId,
    dependsOn: [],
    payload: { ...input },
  });
  await store.upsertLocalCase(local);
  return {
    id: local.id,
    organization_id: local.organization_id,
    identity_id: "",
    case_year: new Date().getFullYear(),
    case_seq: 0,
    case_number: null,
    status: "in_care",
    name: local.name,
    sex: (local.sex as AnimalCaseRow["sex"]) ?? "unknown",
    age_class: (local.age_class as AnimalCaseRow["age_class"]) ?? "unclear",
    found_on: local.found_on ?? null,
    found_place_id: input.foundPlaceId ?? null,
    found_place_text: input.foundPlaceText ?? null,
    intake_reason_note: input.intakeReasonNote ?? null,
    general_notes: input.generalNotes ?? null,
    current_care_site_id: local.current_care_site_id,
    current_place_id: local.current_place_id,
    organization_species_id: local.organization_species_id ?? null,
    species_free_text: local.species_free_text ?? null,
    client_create_id: input.clientCreateId,
    created_at: local.created_at,
    updated_at: local.created_at,
    pendingSync: true,
  };
}

export async function createCasesBatchViaGateway(
  input: CreateAnimalCasesBatchInput
): Promise<AnimalCaseRow[]> {
  if (isOfflineOnline()) {
    try {
      return await CasesApi.createCasesBatchOnline(input);
    } catch (error) {
      if (!isNetworkError(error)) throw error;
    }
  }
  const rows: AnimalCaseRow[] = [];
  for (const animal of input.animals) {
    rows.push(
      await createCaseViaGateway({
        organizationId: input.organizationId,
        clientCreateId: animal.clientCreateId,
        careSiteId: animal.careSiteId,
        placeId: animal.placeId,
        name: animal.name,
        sex: animal.sex,
        ageClass: animal.ageClass,
        foundOn: animal.foundOn,
        foundPlaceId: animal.foundPlaceId,
        foundPlaceText: animal.foundPlaceText,
        intakeReasonIds: animal.intakeReasonIds,
        intakeReasonNote: animal.intakeReasonNote,
        generalNotes: animal.generalNotes,
        organizationSpeciesId: animal.organizationSpeciesId,
        speciesFreeText: animal.speciesFreeText,
      })
    );
  }
  return rows;
}

async function liveRpc() {
  return {
    async submitCareEntry(item: OutboxItem) {
      await CareProtocolApi.submitEntryOnline({
        caseId: String(item.payload.caseId ?? item.caseId),
        clientEntryId: item.clientId,
        recordedAt: String(item.payload.recordedAt),
        events: (item.payload.events as CareEventInput[]) ?? [],
      });
    },
    async createCase(item: OutboxItem) {
      const created = await CasesApi.createCaseOnline(
        item.payload as unknown as CreateAnimalCaseInput
      );
      const store = await getOfflineStore();
      await store.remapCaseId(item.clientId, created.id);
      await store.upsertLocalCase({
        ...created,
        pendingSync: false,
      });
    },
    async createCasesBatch(item: OutboxItem) {
      await CasesApi.createCasesBatchOnline(
        item.payload as unknown as CreateAnimalCasesBatchInput
      );
    },
    async attachMedia(item: OutboxItem) {
      const { uploadPackagingPhoto } = await import(
        "@/api/careProtocol/protocolMediaUpload"
      );
      const mediaId = await uploadPackagingPhoto({
        caseId: String(item.caseId),
        image: {
          uri: String(item.payload.localUri),
          fileName: String(item.payload.fileName ?? "photo.jpg"),
        },
      });
      await CareProtocolApi.submitEntryOnline({
        caseId: String(item.caseId),
        clientEntryId: newClientId(),
        recordedAt: nowIso(),
        events: [
          {
            event_type: "image_ref",
            media_asset_id: mediaId,
          },
        ],
      });
    },
    async pullBundle(
      since: Record<string, string>,
      knownCaseIds: string[]
    ): Promise<CareBundle | null> {
      if (!(await hasAuthSession())) return null;
      const { data, error } = await supabase.rpc("pull_care_bundle", {
        p_since: since,
        p_known_case_ids: knownCaseIds,
      });
      if (error) throw error;
      return (data ?? {}) as CareBundle;
    },
  };
}

let syncInFlight: Promise<SyncResult> | null = null;

export async function runOfflineSync(): Promise<SyncResult> {
  if (isOfflineSyncPaused()) return emptySync();
  if (syncInFlight) return syncInFlight;
  const run = runOfflineSyncBody().finally(() => {
    if (syncInFlight === run) syncInFlight = null;
  });
  syncInFlight = run;
  return run;
}

function syncErrorText(error: unknown): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  if (typeof error === "object" && error !== null && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message;
  }
  return "Bundle-Sync fehlgeschlagen.";
}

function pullNoticeBody(code: string | null): string {
  return code
    ? `${PULL_NOTICE_LEAD} (${code}). ${PULL_NOTICE_TAIL}`
    : `${PULL_NOTICE_LEAD}. ${PULL_NOTICE_TAIL}`;
}

function pullNoticeKey(code: string | null): string {
  return code ? `pull:${code}` : "pull:unknown";
}

/** Warnung je Code, unabhängig davon, ob die Notice schon existiert. */
function warnPullFailure(code: string | null, text: string): void {
  const slot = code ?? "unknown";
  const now = Date.now();
  const last = pullWarnAt.get(slot);
  if (last != null && now - last < PULL_WARN_COOLDOWN_MS) return;
  pullWarnAt.set(slot, now);
  console.warn("offline sync", code ?? "ohne Code", text);
}

async function reportPullFailure(store: OfflineStore, error: unknown): Promise<void> {
  const code = postgresSqlState(error);
  await store.upsertKeyedNotice(pullNoticeKey(code), pullNoticeBody(code), nowIso());
  warnPullFailure(code, syncErrorText(error));
}

async function clearPullNotices(store: OfflineStore): Promise<void> {
  await store.removeNoticesByKeyPrefix("pull:");
}

async function runOfflineSyncBody(): Promise<SyncResult> {
  if (!isOfflineOnline()) return emptySync();
  if (!(await hasAuthSession())) return emptySync();
  const store = await getOfflineStore();
  const rpc = await liveRpc();
  const known = await store.getKnownCaseIds();
  const bundle = await store.getBundle();
  let pullFailed = false;
  try {
    const pulled = await rpc.pullBundle(bundle.cursors ?? {}, known);
    if (pulled && (await hasAuthSession())) {
      await applyPulledBundle(store, pulled);
      await clearPullNotices(store);
    }
  } catch (error) {
    pullFailed = true;
    if (!isNetworkError(error)) await reportPullFailure(store, error);
  }
  if (!(await hasAuthSession())) return { ...emptySync(), pullFailed };
  const drained = await drainOutbox(store, rpc);
  return { ...drained, pullFailed };
}

export async function discardConflict(conflictId: string): Promise<void> {
  const store = await getOfflineStore();
  const conflict = (await store.getConflicts()).find((c) => c.id === conflictId);
  if (!conflict) return;
  await store.deleteConflict(conflictId);
  const item = (await store.getOutbox()).find((o) => o.id === conflict.outboxId);
  if (item) {
    await store.upsertOutbox({
      ...item,
      status: "synced",
      error: "verworfen",
      updatedAt: nowIso(),
    });
  }
}

export async function resubmitOccurrenceAsAdditional(
  conflictId: string,
  reason: string
): Promise<void> {
  const trimmed = reason.trim();
  if (!trimmed) throw new Error("Zusatzgabe braucht eine Begründung.");
  const store = await getOfflineStore();
  const conflict = (await store.getConflicts()).find((c) => c.id === conflictId);
  if (!conflict) return;
  const nextPayload = toAdditionalResubmitPayload(conflict.payload, trimmed);
  await enqueue({
    id: newOutboxId(),
    op: "submit_care_entry",
    status: "pending",
    clientId: String(nextPayload.clientEntryId),
    caseId: conflict.caseId,
    dependsOn: [],
    payload: nextPayload,
  });
  await discardConflict(conflictId);
}

export async function retryConflictWithCurrentExpected(
  conflictId: string,
  expected: { expectedFromPlaceId?: string | null; expectedFromState?: string | null }
): Promise<void> {
  const store = await getOfflineStore();
  const conflict = (await store.getConflicts()).find((c) => c.id === conflictId);
  if (!conflict) return;
  const events = Array.isArray(conflict.payload.events)
    ? (conflict.payload.events as Array<Record<string, unknown>>).map((event) => {
        if (event.event_type === "place_change") {
          const next = { ...event };
          if ("expectedFromPlaceId" in expected) {
            next.expected_from_place_id = expected.expectedFromPlaceId ?? null;
          } else {
            delete next.expected_from_place_id;
          }
          return next;
        }
        if (event.event_type === "diagnosis_change") {
          const next = { ...event };
          if ("expectedFromState" in expected) {
            next.expected_from_state = expected.expectedFromState ?? null;
          } else {
            delete next.expected_from_state;
          }
          return next;
        }
        return event;
      })
    : conflict.payload.events;
  const clientEntryId = newClientId();
  await enqueue({
    id: newOutboxId(),
    op: "submit_care_entry",
    status: "pending",
    clientId: clientEntryId,
    caseId: conflict.caseId,
    dependsOn: [],
    payload: {
      ...conflict.payload,
      clientEntryId,
      events,
    },
  });
  await discardConflict(conflictId);
}

export async function enqueueAttachMedia(input: {
  caseId: string;
  entryClientId: string;
  localUri: string;
  fileName?: string | null;
}): Promise<void> {
  const mediaId = newClientId();
  const store = await getOfflineStore();
  await store.upsertMedia({
    id: mediaId,
    clientMediaId: mediaId,
    caseId: input.caseId,
    entryClientId: input.entryClientId,
    localUri: input.localUri,
    contentType: "image/jpeg",
    status: "pending",
    error: null,
    createdAt: nowIso(),
  });
  await enqueue({
    id: newOutboxId(),
    op: "attach_media",
    status: "pending",
    clientId: mediaId,
    caseId: input.caseId,
    dependsOn: [input.entryClientId],
    payload: {
      localUri: input.localUri,
      fileName: input.fileName ?? "photo.jpg",
      entryClientId: input.entryClientId,
    },
  });
}
